import React, { useEffect, useLayoutEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import Immutable from 'immutable';
import {
  Form,
  FormGroup,
  Row,
  Col,
  Button,
  Container,
  Label,
} from 'reactstrap';
import { Field, change, formValueSelector, reduxForm } from 'redux-form';
import TextInput from '../../../common/forms/input/TextInput';
import {
  isRequired,
  validateIntegerNumber,
  validateLength,
  validateNumber,
} from '../../../../utils/validators';
import Select from '../../../common/forms/select/Select';
import FormItem from '../../../common/forms/FormItem';
import DatePicker from '../../../common/forms/input/DatePicker';
import {
  getActiveSalesSession,
  clearUsers,
  getExpenseAuthorizerUserByCostCenter,
  clearItinerary,
  getItineraryForCounter,
} from '../../../../actions';
import Alert from '../../../common/informative/Alert';
import Loader from '../../../common/Loader';
import BusinessInputGroup from '../../../common/forms/BusinessInputGroup';
import { EXPENSE_PATH } from '../../../../config/paths';
import CustomerField from '../../../common/forms/fields/CustomerField';
import RegisteredBusSelect from '../../../common/forms/select/RegisteredBusSelect';
import VoucherTypeSelect from '../../../common/forms/select/VoucherTypeSelect';
import CostCenterSelect from '../../../common/forms/select/CostCenterSelect';
import ExpenseTypeSelect from '../../../common/forms/select/ExpenseTypeSelect';
import DynamicForm from '../../../common/forms/DynamicForm';
import { ADDITIONAL_ITINERARY_FOOD_COLUMNS } from '../../../../config/dynamicFormFields';
import ItinerarySearchInput from '../../../common/forms/input/ItinerarySearchInput';
import Modal from '../../../common/modal/Modal';
import {
  BOLETA_VOUCHER_TYPE_ID,
  FACTURA_VOUCHER_TYPE_ID,
  RECIBO_HONORARIOS_VOUCHER_TYPE_ID,
  TRIP_MANAGEMENT_COST_CENTER_ID,
} from '../../../../config/constants';

export const validateAlphanumericLength4 = (value) => {
  if (!value) return undefined;

  return /^[A-Za-z0-9]{4}$/.test(value)
    ? undefined
    : 'Debe tener 4 caracteres alfanuméricos';
};

const validateLength8 = validateLength(8, 'dígitos');

const normalizeUppercase = (value) => {
  if (!value) return value;

  return value.toUpperCase();
};

const selector = formValueSelector('ExpenseForm');

export const ExpenseForm = ({
  dispatchGetActiveSalesSession,
  dispatchGetExpenseAuthorizerUserByCostCenter,
  salesSessionId,
  activeSalesSession,
  dispatchClearUsers,
  dispatchChange,
  UserOptions,
  gettingActiveSalesSession,
  loading,
  handleSubmit,
  onSubmit,
  initialValues,
  dispatchClearItinerary,
  dispatchGetItineraryForCounter,
  voucherTypeId,
}) => {
  const [enableControls, setEnableControls] = useState(false);
  const [disabledCostCenter, setDisabledCostCenter] = useState(false);
  const [disabledAuthorizer, setDisabledAuthorizer] = useState(false);
  const [costCenterValue, setCostCenterValue] = useState(null);
  const [showEnableCostCenterCheck, setShowEnableCostCenterCheck] =
    useState(false);
  const [enableCostCenterCheck, setEnableCostCenterCheck] = useState(false);
  const [showDifferentCostCenterConfirm, setShowDifferentCostCenterConfirm] =
    useState(false);
  const [pendingFormValues, setPendingFormValues] = useState(null);
  const [differentCostCenterInfo, setDifferentCostCenterInfo] = useState({
    selectedCostCenter: null,
    agencyCostCenter: null,
    authorizer: null,
  });

  const getOptionValue = (option) => {
    if (!option) return null;

    if (Object.prototype.hasOwnProperty.call(option, 'value')) {
      return option.value;
    }

    return option;
  };

  const getOptionLabel = (option) => {
    if (!option) return null;

    if (Object.prototype.hasOwnProperty.call(option, 'label')) {
      return option.label;
    }

    return null;
  };

  useLayoutEffect(() => {
    dispatchGetActiveSalesSession({ source: EXPENSE_PATH });

    setEnableControls(
      salesSessionId !== 0 && activeSalesSession.get('id') !== salesSessionId,
    );

    if (initialValues && initialValues !== null && initialValues.costCenter) {
      const initialCostCenterId = getOptionValue(initialValues.costCenter);

      setCostCenterValue(initialCostCenterId);

      if (initialCostCenterId) {
        dispatchGetExpenseAuthorizerUserByCostCenter({
          costCenterId: initialCostCenterId,
        });
      }
    }

    return () => dispatchClearUsers();
  }, []);

  const userOptionsKey = UserOptions.map(
    ({ value, authorizerType }) => `${value}-${authorizerType || ''}`,
  ).join('|');

  useEffect(() => {
    if (!costCenterValue) {
      dispatchChange('ExpenseForm', 'authorizerUserId', null);
      setDisabledAuthorizer(false);
      return;
    }

    const principalAuthorizer = UserOptions.find(
      ({ authorizerType }) => authorizerType === 'PRINCIPAL',
    );

    if (principalAuthorizer) {
      dispatchChange('ExpenseForm', 'authorizerUserId', principalAuthorizer);
      setDisabledAuthorizer(true);
      return;
    }

    dispatchChange('ExpenseForm', 'authorizerUserId', null);
    setDisabledAuthorizer(false);
  }, [costCenterValue, userOptionsKey]);

  const getSelectedAuthorizerLabel = (authorizerUserId) => {
    const selectedAuthorizerLabel = getOptionLabel(authorizerUserId);

    if (selectedAuthorizerLabel) return selectedAuthorizerLabel;

    const selectedAuthorizerId = getOptionValue(authorizerUserId);

    const selectedAuthorizer = UserOptions.find(
      ({ value }) => Number(value) === Number(selectedAuthorizerId),
    );

    return selectedAuthorizer ? selectedAuthorizer.label : null;
  };

  const closeDifferentCostCenterConfirm = () => {
    setShowDifferentCostCenterConfirm(false);
    setPendingFormValues(null);
  };

  const confirmDifferentCostCenter = () => {
    setShowDifferentCostCenterConfirm(false);

    if (pendingFormValues) {
      onSubmit(pendingFormValues);
    }

    setPendingFormValues(null);
  };

  const handleSubmitWithCostCenterValidation = (formValues) => {
    const agency = activeSalesSession.get('agency');
    const agencyCostCenter = agency && agency.costCenter;

    const selectedCostCenterId = getOptionValue(formValues.costCenter);
    const agencyCostCenterId = agencyCostCenter ? agencyCostCenter.id : null;

    const isDifferentCostCenter =
      selectedCostCenterId &&
      agencyCostCenterId &&
      Number(selectedCostCenterId) !== Number(agencyCostCenterId);

    if (isDifferentCostCenter) {
      setPendingFormValues(formValues);

      setDifferentCostCenterInfo({
        agencyCostCenter:
          agencyCostCenter.description || `ID ${agencyCostCenterId}`,
        selectedCostCenter:
          getOptionLabel(formValues.costCenter) || `ID ${selectedCostCenterId}`,
        authorizer:
          getSelectedAuthorizerLabel(formValues.authorizerUserId) ||
          'No identificado',
      });

      setShowDifferentCostCenterConfirm(true);
      return;
    }

    onSubmit(formValues);
  };

  const handleExpenseTypeChange = async (expenseTypeOption) => {
    dispatchChange('ExpenseForm', 'authorizerUserId', null);

    setEnableCostCenterCheck(false);
    setDisabledAuthorizer(false);

    const agency = activeSalesSession.get('agency');
    const { costCenter } = agency || {};

    const agencyCostCenter = costCenter
      ? {
          value: costCenter.id,
          label: costCenter.description,
        }
      : null;

    let selectedCostCenter = null;
    let shouldDisableCostCenter = false;
    let shouldShowEnableCheck = false;

    if (expenseTypeOption.isBasicService && agencyCostCenter) {
      selectedCostCenter = agencyCostCenter;
      shouldDisableCostCenter = true;
    } else if (expenseTypeOption.costCenter) {
      selectedCostCenter = expenseTypeOption.costCenter;
      shouldDisableCostCenter = true;
      shouldShowEnableCheck = true;
    } else if (agencyCostCenter) {
      selectedCostCenter = agencyCostCenter;
      shouldDisableCostCenter = true;
      shouldShowEnableCheck = true;
    }

    dispatchChange('ExpenseForm', 'costCenter', selectedCostCenter);

    const selectedCostCenterId =
      selectedCostCenter && selectedCostCenter.value
        ? selectedCostCenter.value
        : null;

    setCostCenterValue(selectedCostCenterId);
    setDisabledCostCenter(shouldDisableCostCenter);
    setShowEnableCostCenterCheck(shouldShowEnableCheck);

    if (selectedCostCenterId) {
      await dispatchGetExpenseAuthorizerUserByCostCenter({
        costCenterId: selectedCostCenterId,
      });
    }
  };

  const handleDocumentCodeBlur = ({ target: { value } }) => {
    if (!value) return;

    const documentCode = value.replace(/\D/g, '').padStart(8, '0').slice(-8);

    setTimeout(() => {
      dispatchChange('ExpenseForm', 'documentCode', documentCode);
    }, 0);
  };

  const handleCostCenterChange = async (costCenterOption) => {
    const costCenterId = costCenterOption ? costCenterOption.value : null;

    setCostCenterValue(costCenterId);

    dispatchChange('ExpenseForm', 'authorizerUserId', null);
    setDisabledAuthorizer(false);

    if (costCenterId) {
      await dispatchGetExpenseAuthorizerUserByCostCenter({ costCenterId });
    }
  };

  const handleEnableCostCenterChange = async (event) => {
    const isEnabled = event.target.checked;

    setEnableCostCenterCheck(isEnabled);

    if (showEnableCostCenterCheck) {
      setDisabledCostCenter(!isEnabled);
    }

    if (!isEnabled && showEnableCostCenterCheck) {
      const agency = activeSalesSession.get('agency');
      const { costCenter } = agency || {};

      if (costCenter) {
        const agencyCostCenter = {
          value: costCenter.id,
          label: costCenter.description,
        };

        dispatchChange('ExpenseForm', 'costCenter', agencyCostCenter);
        setCostCenterValue(costCenter.id);

        await dispatchGetExpenseAuthorizerUserByCostCenter({
          costCenterId: costCenter.id,
        });
      }
    }
  };

  const handleClear = () => {
    dispatchClearItinerary();
  };

  const handleShowItinerarySearch = () => {
    handleClear();
    dispatchChange('ExpenseForm', 'itineraryId', '');
    dispatchChange('ExpenseForm', 'itinerary', '');
  };

  const handleSelectItinerary = ({ itineraryId }) => {
    dispatchGetItineraryForCounter(itineraryId);
    dispatchChange('ExpenseForm', 'itinerary', itineraryId);
  };

  if (gettingActiveSalesSession) return <Loader />;

  if (loading) return <Loader />;

  if (activeSalesSession.isEmpty())
    return (
      <Container>
        <Alert message="No hay ninguna sesión de ventas activa." />
      </Container>
    );

  let sendButton = null;

  if (!enableControls) {
    sendButton = (
      <div className="FormButtonGroup">
        <Button type="submit" color="primary" outline>
          Enviar
        </Button>
      </div>
    );
  }

  const voucherTypeValue =
    voucherTypeId && voucherTypeId.value ? voucherTypeId.value : voucherTypeId;

  const isBusinessRequired = [
    BOLETA_VOUCHER_TYPE_ID,
    FACTURA_VOUCHER_TYPE_ID,
    RECIBO_HONORARIOS_VOUCHER_TYPE_ID,
  ].includes(Number(voucherTypeValue));

  const shouldEditNewAmount =
    initialValues && initialValues.shouldEditNewAmount === true;

  const businessInputGroup = (
    <BusinessInputGroup
      label="Empresa"
      name="business"
      form="ExpenseForm"
      disabled={enableControls}
      validate={isBusinessRequired ? [isRequired] : []}
      labelRequired={isBusinessRequired}
      showDetails
    />
  );

  const shouldShowEnableCostCenterControl =
    showEnableCostCenterCheck || disabledAuthorizer || enableCostCenterCheck;

  return (
    <>
      <Form onSubmit={handleSubmit(handleSubmitWithCostCenterValidation)}>
        <Row>
          <Col>
            <h2>Información básica</h2>
          </Col>
        </Row>

        <FormGroup row>
          <FormItem label="Tipo de egreso" required>
            <Field
              name="expenseTypeId"
              component={ExpenseTypeSelect}
              props={{ isOptionDisabled: true }}
              validate={[isRequired]}
              isDisabled={enableControls}
              onChange={handleExpenseTypeChange}
            />
          </FormItem>
        </FormGroup>

        <FormGroup row>
          <FormItem label="Centro de Costo" required>
            <Field
              name="costCenter"
              component={CostCenterSelect}
              placeholder="Centro de Costo"
              isDisabled={enableControls || disabledCostCenter}
              validate={[isRequired]}
              onChange={handleCostCenterChange}
            />
          </FormItem>
        </FormGroup>

        {shouldShowEnableCostCenterControl && (
          <FormGroup row>
            <FormItem label=" ">
              <Label>
                <input
                  type="checkbox"
                  checked={enableCostCenterCheck}
                  disabled={enableControls}
                  onChange={handleEnableCostCenterChange}
                />{' '}
                Habilitar cambio de centro de costo
              </Label>
            </FormItem>
          </FormGroup>
        )}

        <Row>
          <Col>
            <h2>Documento</h2>
          </Col>
        </Row>

        <FormGroup row>
          <FormItem label="Tipos de comprobante" required>
            <Field
              name="voucherTypeId"
              component={VoucherTypeSelect}
              validate={[isRequired]}
              isDisabled={enableControls}
            />
          </FormItem>
        </FormGroup>

        <FormGroup row>
          <FormItem label="Serie" required>
            <Field
              name="documentSeries"
              component={TextInput}
              maxLength={4}
              type="text"
              placeholder="Serie"
              validate={[isRequired, validateAlphanumericLength4]}
              normalize={normalizeUppercase}
              disabled={enableControls}
            />
          </FormItem>
        </FormGroup>

        <FormGroup row>
          <FormItem label="Correlativo" required>
            <Field
              name="documentCode"
              component={TextInput}
              maxLength={8}
              type="text"
              placeholder="Correlativo"
              validate={[isRequired, validateIntegerNumber, validateLength8]}
              disabled={enableControls}
              onBlur={handleDocumentCodeBlur}
            />
          </FormItem>
        </FormGroup>

        <FormGroup row>
          <FormItem label="Beneficiario" required>
            <CustomerField
              fieldName="payeeCustomerId"
              isDisabled={enableControls}
              validate={[isRequired]}
            />
          </FormItem>
        </FormGroup>

        <FormGroup row>
          <FormItem label="Autorizador" required>
            <Field
              name="authorizerUserId"
              component={Select}
              options={UserOptions}
              validate={[isRequired]}
              isDisabled={
                enableControls || (disabledAuthorizer && !enableCostCenterCheck)
              }
            />
          </FormItem>
        </FormGroup>

        <FormGroup row>
          <FormItem label="Fecha y hora de comprobante">
            <Field
              name="voucherTime"
              props={{
                time: true,
              }}
              component={DatePicker}
              disabled={enableControls}
            />
          </FormItem>
        </FormGroup>

        <Row>
          <Col>
            <h2>Item</h2>
          </Col>
        </Row>

        <FormGroup row>
          <FormItem label="Descripción del Egreso" required>
            <Field
              name="description"
              component={TextInput}
              type="text"
              placeholder="Descripción del Egreso"
              validate={[isRequired]}
              disabled={enableControls}
            />
          </FormItem>
        </FormGroup>

        <FormGroup row>
          <FormItem label="Total" required>
            <Field
              name="amount"
              component={TextInput}
              type="text"
              placeholder="0.00"
              validate={[isRequired, validateNumber]}
              disabled={enableControls || shouldEditNewAmount}
            />
          </FormItem>
        </FormGroup>

        {shouldEditNewAmount && (
          <FormGroup row>
            <FormItem label="Nuevo monto" required>
              <Field
                name="newAmount"
                component={TextInput}
                type="text"
                placeholder="0.00"
                validate={[isRequired, validateNumber]}
                disabled={enableControls}
              />
            </FormItem>
          </FormGroup>
        )}

        <Row>
          <Col>
            <h2>Información adicional</h2>
          </Col>
        </Row>

        {businessInputGroup}

        <FormGroup row>
          <FormItem label="Bus">
            <Field
              name="registeredBusId"
              component={RegisteredBusSelect}
              isDisabled={enableControls}
            />
          </FormItem>
        </FormGroup>

        <Row>
          <Col>
            <h2>Boleto</h2>
          </Col>
        </Row>

        <FormGroup row>
          <FormItem label="Serie de documento">
            <Field
              name="code"
              component={TextInput}
              type="text"
              placeholder="Serie de documento"
              disabled={enableControls}
            />
          </FormItem>
        </FormGroup>

        <FormGroup row>
          <FormItem label="Código de documento">
            <Field
              name="number"
              component={TextInput}
              type="text"
              placeholder="Código de documento"
              disabled={enableControls}
              validate={[validateIntegerNumber]}
            />
          </FormItem>
        </FormGroup>

        {Number(costCenterValue) === TRIP_MANAGEMENT_COST_CENTER_ID && (
          <>
            <Row>
              <Col>
                <h2>Detalle de Alimentacion Adicional</h2>
              </Col>
            </Row>

            <FormGroup row>
              <FormItem label="Itinerario">
                <Field
                  name="itineraryId"
                  component={ItinerarySearchInput}
                  placeholder="Itinerario"
                  handleSelectItinerary={handleSelectItinerary}
                  handleShowItinerarySearch={handleShowItinerarySearch}
                  handleClear={handleClear}
                  props={{
                    form: 'ExpenseForm',
                  }}
                />
              </FormItem>
            </FormGroup>

            <FormGroup row>
              <FormItem label="Observaciones">
                <Field
                  name="observation"
                  component={TextInput}
                  type="textarea"
                  placeholder="Observaciones"
                  disabled={enableControls}
                />
              </FormItem>
            </FormGroup>

            <h3>Tipo de Alimentación</h3>

            <DynamicForm
              name="itineraryAdditionalFoodTypeList"
              columns={ADDITIONAL_ITINERARY_FOOD_COLUMNS}
              disabled={enableControls}
            />
          </>
        )}

        {sendButton}
      </Form>

      <Modal
        show={showDifferentCostCenterConfirm}
        title="Centro de costo diferente"
        body={
          <div>
            <p>
              El centro de costo que está registrando en el egreso es diferente
              al centro de costo de su agencia actual.
            </p>

            <p>
              Agencia actual:{' '}
              <strong>{differentCostCenterInfo.agencyCostCenter}</strong>
            </p>

            <p>
              Centro de costo seleccionado:{' '}
              <strong>{differentCostCenterInfo.selectedCostCenter}</strong>
            </p>

            <p>
              Autorizador seleccionado:{' '}
              <strong>{differentCostCenterInfo.authorizer}</strong>
            </p>

            <p>¿Desea continuar con el registro del egreso?</p>
          </div>
        }
        onClickCancel={closeDifferentCostCenterConfirm}
        onClickClose={closeDifferentCostCenterConfirm}
        cancelButtonText="Cancelar"
        cancelButtonColor="secondary"
        confirmButtonText="Continuar"
        confirmButtonColor="primary"
        onClickConfirm={confirmDifferentCostCenter}
      />
    </>
  );
};

const mapStateToProps = (state) => ({
  UserOptions: state.UserUnit.User.getIn(['all', 'content', 'content']).map(
    (user) => ({
      value: user.userId,
      label: user.fullName,
      authorizerType: user.authorizerType,
    }),
  ),
  gettingActiveSalesSession: state.SalesUnit.SalesSession.getIn([
    'active',
    'loading',
  ]),
  loading: !state.SalesUnit.Expense.getIn(['current', 'activity']).isEmpty(),
  activeSalesSession: state.SalesUnit.SalesSession.getIn(['active', 'content']),
  voucherTypeId: selector(state, 'voucherTypeId'),
});

const mapDispatchToProps = {
  dispatchGetExpenseAuthorizerUserByCostCenter:
    getExpenseAuthorizerUserByCostCenter,
  dispatchGetActiveSalesSession: getActiveSalesSession,
  dispatchClearUsers: clearUsers,
  dispatchChange: change,
  dispatchClearItinerary: clearItinerary,
  dispatchGetItineraryForCounter: getItineraryForCounter,
};

ExpenseForm.propTypes = {
  dispatchChange: PropTypes.func.isRequired,
  salesSessionId: PropTypes.number,
  dispatchGetActiveSalesSession: PropTypes.func.isRequired,
  gettingActiveSalesSession: PropTypes.bool.isRequired,
  loading: PropTypes.bool.isRequired,
  activeSalesSession: PropTypes.instanceOf(Immutable.Map).isRequired,
  handleSubmit: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  dispatchGetExpenseAuthorizerUserByCostCenter: PropTypes.func.isRequired,
  dispatchClearUsers: PropTypes.func.isRequired,
  dispatchClearItinerary: PropTypes.func.isRequired,
  dispatchGetItineraryForCounter: PropTypes.func.isRequired,
  UserOptions: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.number,
      label: PropTypes.string,
      authorizerType: PropTypes.string,
    }),
  ).isRequired,
  initialValues: PropTypes.shape({
    costCenter: PropTypes.shape({
      value: PropTypes.number,
      label: PropTypes.string,
    }),
    shouldEditNewAmount: PropTypes.bool,
  }),
  voucherTypeId: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.shape({
      value: PropTypes.number,
      label: PropTypes.string,
    }),
  ]),
};

ExpenseForm.defaultProps = {
  salesSessionId: 0,
  initialValues: null,
  voucherTypeId: null,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(
  reduxForm({
    form: 'ExpenseForm',
  })(ExpenseForm),
);
